import React from "react";

import { Toolbar, ToolbarGroup, Tooltip, Button, makeStyles, Text } from "@fluentui/react-components";
import { Add16Regular, Add16Filled, ArrowClockwise16Regular, ArrowClockwise16Filled, ArrowLeft16Regular, ArrowLeft16Filled, bundleIcon, Edit16Filled, Edit16Regular } from "@fluentui/react-icons";

import { useCategoryContext } from "../context/CategoryContext";
import { useCategoryTabStore } from "../store/CategoryTabStore";
import { useCategoryDrawerStore } from "../store/CategoryDrawerStore";
import { useCategoryGridStore } from "../store/CategoryGridStore";
import { useOptionDrawerStore } from "../store/OptionDrawerStore";
import { useOptionGridStore } from "../store/OptionGridStore";

const BackBundle = bundleIcon(ArrowLeft16Filled, ArrowLeft16Regular);
const AddBundle = bundleIcon(Add16Filled, Add16Regular);
const EditBundle = bundleIcon(Edit16Filled, Edit16Regular);
const RefreshBundle = bundleIcon(ArrowClockwise16Filled, ArrowClockwise16Regular);

const useStyles = makeStyles({
    toolBar: {
        justifyContent: "space-between",
        padding: "5px 0px",
    },
});

export default function CategoryToolbar() {
    const styles = useStyles();

    const { refetchCategories, refetchOptions, isCategoriesFetching, isOptionsFetching } = useCategoryContext();
    const { closeOptions, selectedCategory, isShowOptions } = useCategoryTabStore();
    const { selectedCategories } = useCategoryGridStore();
    const { selectedOptions } = useOptionGridStore();

    const { openNew: openNewCategory, openEdit: openEditCategory } = useCategoryDrawerStore();
    const { openNew: openNewOption, openEdit: openEditOption } = useOptionDrawerStore();

    const handleRefresh = React.useCallback(() => {
        if (!isShowOptions) {
            refetchCategories();
        }
        else {
            refetchOptions();
        }
    }, [isShowOptions, refetchCategories, refetchOptions]);

    const handleEdit = React.useCallback(() => {
        if (!isShowOptions) {
            openEditCategory(selectedCategories[0])
        }
        else {
            openEditOption(selectedOptions[0])
        }
    }, [isShowOptions, openEditCategory, selectedCategories, openEditOption, selectedOptions]);

    return (
        <Toolbar
            aria-label='Category Toolbar'
            size='medium'
            className={styles.toolBar}
        >
            <ToolbarGroup>
                {
                    isShowOptions &&
                    <>
                        <Tooltip content='Back' relationship='label' withArrow={true}>
                            <Button
                                aria-label='Back'
                                shape='circular'
                                appearance='subtle'
                                icon={<BackBundle />}
                                onClick={closeOptions}
                            />
                        </Tooltip>
                        <Text as="h3" weight="semibold">
                            Category: {selectedCategory?.name}
                        </Text>
                    </>
                }

            </ToolbarGroup>

            <ToolbarGroup>
                <Button
                    aria-label='Add'
                    shape='circular'
                    appearance='subtle'
                    icon={<AddBundle />}
                    onClick={!isShowOptions ? openNewCategory : openNewOption}
                >Add</Button>

                <Button
                    aria-label="Edit"
                    shape="circular"
                    appearance="subtle"
                    icon={<EditBundle />}
                    onClick={handleEdit}
                    disabled={isShowOptions ? selectedOptions.length !== 1 : selectedCategories.length !== 1}
                >Edit</Button>

                <Button
                    aria-label='Refresh'
                    shape='circular'
                    appearance='subtle'
                    icon={<RefreshBundle />}
                    onClick={handleRefresh}
                    disabled={isCategoriesFetching || isOptionsFetching}
                >Refresh</Button>
            </ToolbarGroup>
        </Toolbar>
    );
}
